<!-- src/components/appointments/AppointmentCalendar.vue -->
<template>
    <div class="appointment-calendar">
        <!-- Calendar toolbar -->
        <div class="d-flex justify-content-between mb-3">
            <div class="btn-group">
                <button class="btn btn-outline-primary" @click="changeView('dayGridMonth')"
                    :class="{ active: currentView === 'dayGridMonth' }">
                    Mes
                </button>
                <button class="btn btn-outline-primary" @click="changeView('timeGridWeek')"
                    :class="{ active: currentView === 'timeGridWeek' }">
                    Semana
                </button>
                <button class="btn btn-outline-primary" @click="changeView('timeGridDay')"
                    :class="{ active: currentView === 'timeGridDay' }">
                    Día
                </button>
            </div>

            <div class="d-flex align-items-center">
                <button class="btn btn-outline-secondary me-2" @click="goToPrev">
                    <i class="bi bi-chevron-left"></i>
                </button>
                <button class="btn btn-outline-secondary me-2" @click="goToToday">
                    Hoy
                </button>
                <button class="btn btn-outline-secondary me-2" @click="goToNext">
                    <i class="bi bi-chevron-right"></i>
                </button>
                <h5 class="mb-0 ms-2">{{ currentDate }}</h5>
            </div>

            <button class="btn btn-primary" @click="$emit('add-appointment')">
                <i class="bi bi-plus-circle me-1"></i> Nueva Cita
            </button>
        </div>

        <!-- Calendar -->
        <FullCalendar ref="calendar" :options="calendarOptions" />

        <!-- Event Modal -->
        <div class="modal fade" :class="{ show: showEventModal, 'd-block': showEventModal }" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Cita</h5>
                        <button type="button" class="btn-close" @click="closeEventModal"></button>
                    </div>
                    <div class="modal-body" v-if="selectedEvent">
                        <div class="mb-3">
                            <strong>Paciente: </strong>
                            <router-link :to="`/patients/${selectedEvent.extendedProps.patientId}`">
                                {{ selectedEvent.title }}
                            </router-link>
                        </div>
                        <div class="mb-3">
                            <strong>Fecha: </strong>{{ formatDate(selectedEvent.start) }}
                        </div>
                        <div class="mb-3">
                            <strong>Hora: </strong>{{ formatTime(selectedEvent.start) }}
                        </div>
                        <div class="mb-3">
                            <strong>Duración: </strong>{{ formatDuration(selectedEvent.extendedProps.duracion) }}
                        </div>
                        <div class="mb-3">
                            <strong>Tratamiento: </strong>{{ selectedEvent.extendedProps.treatment }}
                        </div>
                        <div class="mb-3">
                            <strong>Estado: </strong>
                            <span :class="getStatusClass(selectedEvent.extendedProps.status)"
                                class="badge rounded-pill">
                                {{ selectedEvent.extendedProps.status }}
                            </span>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeEventModal">Cerrar</button>
                        <router-link v-if="selectedEvent" :to="`/appointments/${selectedEvent.id}`"
                            class="btn btn-primary">
                            Ver detalles
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal backdrop -->
        <div v-if="showEventModal" class="modal-backdrop fade show"></div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { appointmentService } from '@/services/api';

export default {
    name: 'AppointmentCalendar',
    components: {
        FullCalendar
    },
    emits: ['add-appointment'],
    setup() {
        const calendar = ref(null);
        const events = ref([]);
        const currentView = ref('dayGridMonth');
        const showEventModal = ref(false);
        const selectedEvent = ref(null);
        const currentDate = ref('');

        const calendarOptions = computed(() => ({
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
            initialView: currentView.value,
            headerToolbar: false, // We're creating our own toolbar
            events: events.value,
            locale: 'es',
            firstDay: 1, // Monday
            slotMinTime: '08:00:00',
            slotMaxTime: '19:00:00',
            businessHours: {
                daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
                startTime: '09:00',
                endTime: '18:00',
            },
            eventTimeFormat: {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            },
            nowIndicator: true,
            dayMaxEvents: true,
            eventClick: handleEventClick,
            dateClick: handleDateClick,
            datesSet: handleDatesSet,
            eventClassNames: function (arg) {
                // Add custom styling based on appointment status with better contrast
                const status = arg.event.extendedProps.status;

                if (status === 'programada') return ['event-scheduled']; // Light blue with dark text
                if (status === 'completada') return ['event-completed']; // Green with white text
                if (status === 'inasistencia') return ['event-cancelled']; // Red with white text
                if (status === 'confirmada') return ['event-rescheduled']; // Yellow with dark text

                return [];
            }
        }));

        onMounted(() => {
            fetchAppointments();
        });

        async function fetchAppointments() {
            try {
                const response = await appointmentService.getAll();
                // Transform appointments into events
                events.value = response.data.map(app => ({
                    id: app.id,
                    title: app.patient_name,
                    start: `${app.fecha}T${app.hora}`,
                    end: app.duracion
                        ? calculateEndTimeWithDuration(app.fecha, app.hora, app.duracion)
                        : calculateEndTime(app.fecha, app.hora),
                    extendedProps: {
                        patientId: app.patient,
                        treatment: app.tipo_tratamiento,
                        status: app.estado,
                        duracion: app.duracion || 30 // Store duration with default of 30 minutes
                    }
                }));
            } catch (error) {
                console.error('Error fetching appointments:', error);
            }
        }

        // Add this new function to calculate end time with duration
        function calculateEndTimeWithDuration(date, time, duration) {
            const start = new Date(`${date}T${time}`);
            const end = new Date(start.getTime() + duration * 60000); // Add duration in minutes
            return end.toISOString();
        }

        function calculateEndTime(date, time) {
            // Default appointment length: 30 minutes
            const start = new Date(`${date}T${time}`);
            const end = new Date(start.getTime() + 30 * 60000); // Add 30 minutes
            return end.toISOString();
        }

        function handleEventClick(info) {
            selectedEvent.value = info.event;
            showEventModal.value = true;
        }

        // Add this new function to calculate end time with duration
        function calculateEndTimeWithDuration(date, time, duration) {
            const start = new Date(`${date}T${time}`);
            const end = new Date(start.getTime() + duration * 60000); // Add duration in minutes
            return end.toISOString();
        }

        function handleDateClick(info) {
            // When a date is clicked, emit an event to create a new appointment
            // or navigate to the appointment creation page with the selected date
            console.log('Date clicked:', info.dateStr);
        }

        function handleDatesSet(info) {
            // Update the current date title
            const view = calendar.value.getApi();
            const viewTitle = view.view.title;
            currentDate.value = viewTitle;
        }

        function closeEventModal() {
            showEventModal.value = false;
            selectedEvent.value = null;
        }

        function changeView(view) {
            currentView.value = view;
            const calendarApi = calendar.value.getApi();
            calendarApi.changeView(view);
        }

        function goToPrev() {
            const calendarApi = calendar.value.getApi();
            calendarApi.prev();
        }

        function goToNext() {
            const calendarApi = calendar.value.getApi();
            calendarApi.next();
        }

        function goToToday() {
            const calendarApi = calendar.value.getApi();
            calendarApi.today();
        }

        function formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('es-ES');
        }

        function formatTime(date) {
            if (!date) return '';
            return new Date(date).toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function getStatusClass(status) {
            const statusClasses = {
                'programada': 'bg-info',
                'completada': 'bg-success',
                'inasistencia': 'bg-danger',
                'confirmada': 'bg-warning'
            };
            return statusClasses[status] || 'bg-secondary';
        }

        function formatDuration(minutes) {
            if (!minutes) return '30 minutos'; // Default

            if (minutes < 60) {
                return `${minutes} minutos`;
            }

            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;

            if (remainingMinutes === 0) {
                return `${hours} hora${hours > 1 ? 's' : ''}`;
            } else {
                return `${hours} hora${hours > 1 ? 's' : ''} y ${remainingMinutes} minutos`;
            }
        }

        return {
            calendar,
            calendarOptions,
            currentView,
            currentDate,
            selectedEvent,
            showEventModal,
            closeEventModal,
            changeView,
            goToPrev,
            goToNext,
            goToToday,
            formatDate,
            formatTime,
            getStatusClass,
            formatDuration
        };
    }
};
</script>

<style scoped>
.appointment-calendar {
    height: 100%;
}

/* Make all text in calendar black */
:deep(.fc) {
  color: black;
}

/* Make sure day numbers are black */
:deep(.fc-daygrid-day-number) {
  color: black;
}

/* Make headers black */
:deep(.fc-col-header-cell) {
  color: black;
}

/* Make time slots black */
:deep(.fc-timegrid-slot-label) {
  color: black;
}

/* Make "more" links black */
:deep(.fc-daygrid-more-link) {
  color: black;
}

/* Event styling with proper text contrast */
:deep(.event-scheduled) {
    background-color: #c0e3ff !important; /* Lighter blue */
    border-color: #86c5ff !important;
    color: #104a7d !important; /* Dark blue text for better contrast */
}

:deep(.event-completed) {
    background-color: #28a745 !important; /* Green */
    border-color: #1e7e34 !important;
    color: white !important; /* White text works on dark green */
}

:deep(.event-cancelled) {
    background-color: #dc3545 !important; /* Red */
    border-color: #bd2130 !important;
    color: white !important; /* White text works on red */
    text-decoration: line-through;
}

:deep(.event-rescheduled) {
    background-color: #ffdb7d !important; /* Lighter yellow */
    border-color: #ffc107 !important;
    color: #856404 !important; /* Dark text for light yellow background */
}

/* Make the text in events bold for better readability */
:deep(.fc-event-title) {
    font-weight: bold;
}

/* Increase the contrast for the event time */
:deep(.fc-event-time) {
    font-weight: bold;
    opacity: 0.9;
}

/* Explicitly ensure time and title elements in events have appropriate colors */
:deep(.event-scheduled .fc-event-time),
:deep(.event-scheduled .fc-event-title) {
    color: #104a7d !important;
}

:deep(.event-completed .fc-event-time),
:deep(.event-completed .fc-event-title) {
    color: white !important;
}

:deep(.event-cancelled .fc-event-time),
:deep(.event-cancelled .fc-event-title) {
    color: white !important;
}

:deep(.event-rescheduled .fc-event-time),
:deep(.event-rescheduled .fc-event-title) {
    color: #856404 !important;
}

.modal {
    background-color: rgba(0, 0, 0, 0.5);
}
</style>